/**
 * LoopMarker Component
 * Draggable markers for setting loop points within audio clips
 */
import { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

export const LoopMarker = ({
  type = 'start', // 'start' | 'end'
  position = 0, // Position as percentage (0-100)
  onDrag,
  onDragStart,
  onDragEnd,
  isDragging = false,
}) => {
  const markerRef = useRef(null);
  const containerRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    onDragStart?.();
    
    const parent = markerRef.current?.parentElement;
    if (!parent) return;
    
    const handleMouseMove = (moveEvent) => {
      const rect = parent.getBoundingClientRect();
      const x = moveEvent.clientX - rect.left;
      const newPosition = Math.max(0, Math.min((x / rect.width) * 100, 100));
      onDrag?.(newPosition);
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      onDragEnd?.();
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [onDrag, onDragStart, onDragEnd]);

  // Touch support
  const handleTouchStart = useCallback((e) => {
    e.stopPropagation();
    onDragStart?.();
    
    const parent = markerRef.current?.parentElement;
    if (!parent) return;
    
    const handleTouchMove = (moveEvent) => {
      const touch = moveEvent.touches[0];
      const rect = parent.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const newPosition = Math.max(0, Math.min((x / rect.width) * 100, 100));
      onDrag?.(newPosition);
    };
    
    const handleTouchEnd = () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      onDragEnd?.();
    };
    
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  }, [onDrag, onDragStart, onDragEnd]);

  const isStart = type === 'start';

  return (
    <motion.div
      ref={markerRef}
      className="absolute top-0 bottom-0 z-10 cursor-ew-resize group"
      style={{ left: `${position}%` }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      data-testid={`loop-marker-${type}`}
    >
      {/* Vertical line */}
      <div 
        className={`
          w-1 h-full 
          bg-amber-400 
          shadow-[0_0_8px_rgba(251,191,36,0.6)]
          transition-all
          group-hover:w-1.5 group-hover:bg-amber-300
        `}
      />
      
      {/* Triangle handle at top */}
      <div 
        className={`
          absolute -top-2 
          w-0 h-0 
          border-l-[6px] border-l-transparent 
          border-r-[6px] border-r-transparent 
          border-t-[8px] border-t-amber-400
          -translate-x-[5px]
          transition-all
          group-hover:border-t-amber-300
        `}
      />
      
      {/* Label */}
      <div 
        className={`
          absolute -top-5 
          px-1 py-0.5 
          bg-amber-900/80 
          rounded text-[9px] font-mono text-amber-200
          whitespace-nowrap
          opacity-0 group-hover:opacity-100
          transition-opacity
          ${isStart ? '-translate-x-1/2 left-1/2' : '-translate-x-1/2 left-1/2'}
        `}
      >
        {isStart ? 'L' : 'R'}
      </div>
    </motion.div>
  );
};

export default LoopMarker;
